/**
 * ReleasesManager — the book creator's view of who holds their books.
 *
 * READ ONLY for now, by instruction: prove the read routes against real data before building
 * grant/revoke. GET /api/recipients and GET /api/recipients/:userId are the whole surface.
 *
 * Creator-scoped, which is the point. The existing client-downloads page is admin-only and
 * quirkmaker is the only admin, so a tab there would inherit the blocker these endpoints exist to
 * remove. The server scopes by traversal from the caller's own user node; this page sends no id
 * that could widen that.
 *
 * Why it may look empty on an admin login: a reader joins a creator's recipients when a key is
 * REDEEMED, and the owner is derived Book -> LitGroup -> User. The books sit under bookcreator-1
 * while keys are issued by quirkmaker, so readers land in bookcreator-1's list.
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  Container, Header, Table, Message, Loader, Segment, Label, Icon, Button,
} from 'semantic-ui-react';
import { apiClient } from '../../../apiTools/apiClient';

const fmtDate = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? null
    : d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
};

const fmtWhen = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const mins = Math.round((Date.now() - d.getTime()) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return mins + ' min ago';
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return hrs + (hrs === 1 ? ' hour ago' : ' hours ago');
  const days = Math.round(hrs / 24);
  if (days < 30) return days + (days === 1 ? ' day ago' : ' days ago');
  return fmtDate(iso);
};

const errText = (err) => (
  err?.response?.data?.error || err?.message || 'Could not load recipients.'
);

/** One expanded recipient: their grants and their recent accesses. */
const RecipientDetail = ({ detail, loading }) => {
  if (loading) return <Loader active inline size="tiny" />;
  if (!detail) return null;

  return (
    <div style={{ padding: '0.5rem 0 0.75rem 0' }}>
      <Header as="h5" style={{ marginBottom: '0.35rem' }}>Books they hold</Header>
      {detail.grants.length === 0 ? (
        <p style={{ color: '#767676' }}>No approved books.</p>
      ) : (
        <Table basic="very" compact size="small">
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell>Title</Table.HeaderCell>
              <Table.HeaderCell>Kind</Table.HeaderCell>
              <Table.HeaderCell>Approved</Table.HeaderCell>
              <Table.HeaderCell>Expires</Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {detail.grants.map((g) => (
              <Table.Row key={g.releaseUuid}>
                <Table.Cell>{g.title || g.releaseUuid}</Table.Cell>
                <Table.Cell>{g.kind || '—'}</Table.Cell>
                <Table.Cell>{fmtDate(g.approvedAt) || '—'}</Table.Cell>
                <Table.Cell>{fmtDate(g.expiresAt) || '—'}</Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      )}

      <Header as="h5" style={{ marginBottom: '0.35rem', marginTop: '1rem' }}>
        Recent downloads
      </Header>
      {detail.recent.length === 0 ? (
        <p style={{ color: '#767676' }}>
          Nothing recorded. Access logging started recently — earlier downloads are not recoverable.
        </p>
      ) : (
        <Table basic="very" compact size="small">
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell>When</Table.HeaderCell>
              <Table.HeaderCell>Book</Table.HeaderCell>
              <Table.HeaderCell>File</Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {detail.recent.map((a, i) => (
              <Table.Row key={a.at + ':' + i}>
                <Table.Cell>{fmtWhen(a.at) || '—'}</Table.Cell>
                <Table.Cell>{a.bookTitle || a.title || '—'}</Table.Cell>
                <Table.Cell style={{ wordBreak: 'break-all' }}>{a.fileName || '—'}</Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      )}
    </div>
  );
};

const ReleasesManager = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [details, setDetails] = useState({});
  const [detailLoading, setDetailLoading] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiClient.get('/recipients');
      setRows(res.data?.recipients || []);
    } catch (err) {
      setError(errText(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggle = async (userId) => {
    if (openId === userId) { setOpenId(null); return; }
    setOpenId(userId);
    if (details[userId]) return;
    setDetailLoading(userId);
    try {
      const res = await apiClient.get('/recipients/' + encodeURIComponent(userId));
      const r = res.data?.recipient;
      if (r) setDetails((prev) => ({ ...prev, [userId]: r }));
    } catch (err) {
      setError(errText(err));
    } finally {
      setDetailLoading(null);
    }
  };

  return (
    <Container style={{ marginTop: '2rem', marginBottom: '3rem' }}>
      <Header as="h2">
        Releases Manager
        <Header.Subheader>
          Readers who have redeemed a key for one of your books, what they hold, and what they took.
        </Header.Subheader>
      </Header>

      <Message info size="small">
        <Message.Header>History only, for now</Message.Header>
        <p>
          This view is read-only. Approving and withdrawing books is the next step.
        </p>
        <p>
          A reader joins this list when they redeem a key, and they are filed under the account that
          OWNS the book — not the account that issued the key. If you expect readers here and see
          none, check that you are signed in as the owner of the literary group.
        </p>
      </Message>

      {error && (
        <Message negative onDismiss={() => setError(null)}>
          <Message.Header>Could not load</Message.Header>
          <p>{error}</p>
        </Message>
      )}

      <Button
        basic
        size="small"
        icon
        labelPosition="left"
        onClick={load}
        disabled={loading}
        style={{ marginBottom: '1rem' }}
      >
        <Icon name="refresh" />
        Refresh
      </Button>

      {loading ? (
        <Segment padded="very" textAlign="center">
          <Loader active inline="centered">Loading recipients…</Loader>
        </Segment>
      ) : rows.length === 0 ? (
        <Segment placeholder textAlign="center">
          <Header as="h4" icon>
            <Icon name="users" />
            No recipients yet
            <Header.Subheader>
              A reader appears here the first time they redeem one of your keys.
            </Header.Subheader>
          </Header>
        </Segment>
      ) : (
        <Table selectable compact>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell />
              <Table.HeaderCell>Reader</Table.HeaderCell>
              <Table.HeaderCell>Email</Table.HeaderCell>
              <Table.HeaderCell textAlign="center">Books</Table.HeaderCell>
              <Table.HeaderCell textAlign="center">Downloads</Table.HeaderCell>
              <Table.HeaderCell>Last activity</Table.HeaderCell>
              <Table.HeaderCell>Joined</Table.HeaderCell>
              <Table.HeaderCell>Status</Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {rows.map((r) => (
              <React.Fragment key={r.userId}>
                <Table.Row style={{ cursor: 'pointer' }} onClick={() => toggle(r.userId)}>
                  <Table.Cell collapsing>
                    <Icon name={openId === r.userId ? 'chevron down' : 'chevron right'} />
                  </Table.Cell>
                  <Table.Cell>{r.name || '—'}</Table.Cell>
                  <Table.Cell style={{ wordBreak: 'break-all' }}>{r.email || '—'}</Table.Cell>
                  <Table.Cell textAlign="center">{r.grants}</Table.Cell>
                  {/*
                    ⚠️ Blank, not 0, when nothing is recorded: access logging began recently, so a
                    zero here would be a claim the data cannot support.
                  */}
                  <Table.Cell textAlign="center">{r.accesses > 0 ? r.accesses : ''}</Table.Cell>
                  <Table.Cell>{fmtWhen(r.lastAccess) || '—'}</Table.Cell>
                  <Table.Cell>{fmtDate(r.createdAt) || '—'}</Table.Cell>
                  <Table.Cell>
                    {r.isActive
                      ? <Label basic color="green" size="tiny">Active</Label>
                      : <Label basic color="grey" size="tiny">Suspended</Label>}
                    {r.mayContact && (
                      <Label basic size="tiny" style={{ marginLeft: '0.25rem' }}>
                        <Icon name="mail" />
                        OK to contact
                      </Label>
                    )}
                  </Table.Cell>
                </Table.Row>
                {openId === r.userId && (
                  <Table.Row>
                    <Table.Cell colSpan="8" style={{ background: '#fafafa' }}>
                      <RecipientDetail
                        detail={details[r.userId]}
                        loading={detailLoading === r.userId}
                      />
                    </Table.Cell>
                  </Table.Row>
                )}
              </React.Fragment>
            ))}
          </Table.Body>
        </Table>
      )}
    </Container>
  );
};

export default ReleasesManager;
